import React, { useRef, useState, useCallback, useEffect } from 'react';
import { arrayOf, bool, object, string } from 'prop-types';
import classNames from 'classnames';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';

import { NamedLink } from '../../components';
import { formatMoney, convertUnitToSubUnit, unitDivisor } from '../../util/currency';
import { types as sdkTypes } from '../../util/sdkLoader';
import { createSlug } from '../../util/urlHelpers';
import { getListingPriceWithReolinkPromoMaybe } from '../../util/reolinkPromoPriceDisplay';

import css from './ProductCarouselSection.module.css';

const CarouselProductImage = ({ src, alt, isEager }) => {
  const [loaded, setLoaded] = useState(false);
  const imgRef = useRef(null);

  useEffect(() => {
    if (imgRef.current?.complete) {
      setLoaded(true);
    }
  }, [src]);

  return (
    <img
      ref={imgRef}
      src={src}
      alt={alt}
      className={classNames(css.carouselProductImage, loaded && css.carouselProductImageLoaded)}
      loading={isEager ? 'eager' : 'lazy'}
      decoding="async"
      width="200"
      height="200"
      onLoad={() => setLoaded(true)}
    />
  );
};

const ProductCarouselSection = props => {
  const {
    products = [],
    inProgress,
    error,
    title,
    subtitle,
    categoryLink,
    viewAllLabel,
    config,
    intl,
  } = props;

  const location = useLocation();
  const currentUser = useSelector(state => state.user?.currentUser);

  const carouselRef = useRef(null);
  const cardWidthCacheRef = useRef(null);
  const checkScrollRef = useRef(null);

  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);

  checkScrollRef.current = () => {
    if (carouselRef.current) {
      requestAnimationFrame(() => {
        if (carouselRef.current) {
          const { scrollLeft, scrollWidth, clientWidth } = carouselRef.current;
          setCanScrollLeft(scrollLeft > 0);
          setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 1);
        }
      });
    }
  };

  // Attach scroll listener
  useEffect(() => {
    const el = carouselRef.current;
    if (!el) return;
    const handler = () => checkScrollRef.current?.();
    el.addEventListener('scroll', handler, { passive: true });
    checkScrollRef.current?.();
    return () => el.removeEventListener('scroll', handler);
  }, [products]);

  // ResizeObserver to cache card width efficiently
  useEffect(() => {
    const el = carouselRef.current;
    if (!el || !('ResizeObserver' in window)) return;
    const card = el.querySelector(`.${css.carouselProductCard}`);
    if (!card) return;
    const ro = new ResizeObserver(() => {
      requestAnimationFrame(() => {
        cardWidthCacheRef.current = card.offsetWidth;
      });
    });
    ro.observe(card);
    cardWidthCacheRef.current = card.offsetWidth;
    return () => ro.disconnect();
  }, [products]);

  const scrollLeft = useCallback(() => {
    if (carouselRef.current) {
      const cardWidth = cardWidthCacheRef.current || 200;
      carouselRef.current.scrollBy({ left: -(cardWidth + 20), behavior: 'smooth' });
    }
  }, []);

  const scrollRight = useCallback(() => {
    if (carouselRef.current) {
      const cardWidth = cardWidthCacheRef.current || 200;
      carouselRef.current.scrollBy({ left: cardWidth + 20, behavior: 'smooth' });
    }
  }, []);

  const viewAllHref = categoryLink?.startsWith('/')
    ? categoryLink
    : categoryLink
    ? `/products/${categoryLink}`
    : null;
  const viewAllContextLabel =
    title
      ?.split(' ')
      .slice(0, 4)
      .join(' ') || 'la sélection';
  const viewAllHeaderText = viewAllLabel || `Voir ${viewAllContextLabel}`;
  const viewAllCardText = viewAllLabel || `Voir ${viewAllContextLabel.toLowerCase()}`;

  const getBrandDisplayName = brandValue => {
    if (!brandValue) return null;
    const brandStr = Array.isArray(brandValue) ? brandValue[0] : brandValue;
    if (typeof brandStr !== 'string') return brandStr;
    const brandFieldConfig = config?.listing?.listingFields?.find(f => f.key === 'brand');
    const brandOption = (brandFieldConfig?.enumOptions || []).find(o => o.option === brandStr);
    if (brandOption) return brandOption.label;
    return brandStr
      .split('-')
      .map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
      .join(' ');
  };

  const getDisplayValue = (fieldKey, value) => {
    const fieldConfig = config?.listing?.listingFields?.find(f => f.key === fieldKey);
    if (!fieldConfig?.enumOptions) {
      return typeof value === 'string' ? value.charAt(0).toUpperCase() + value.slice(1) : value;
    }
    if (Array.isArray(value)) {
      return value.map(v => {
        const opt = fieldConfig.enumOptions.find(o => o.option === v);
        return opt ? opt.label : typeof v === 'string' ? v.charAt(0).toUpperCase() + v.slice(1) : v;
      });
    }
    const opt = fieldConfig.enumOptions.find(o => o.option === value);
    return opt
      ? opt.label
      : typeof value === 'string'
      ? value.charAt(0).toUpperCase() + value.slice(1)
      : value;
  };

  const formatConnectivity = connectivityArray => {
    if (!Array.isArray(connectivityArray) || connectivityArray.length === 0) return null;
    const vals = getDisplayValue('connectivity', connectivityArray);
    if (vals.length === 1) return vals[0];
    if (vals.length === 2) return `${vals[0]} & ${vals[1]}`;
    const rest = vals.length - 1;
    return `${vals[0]} & ${rest} ${rest === 1 ? 'autre' : 'autres'}`;
  };

  const renderCard = (listing, index) => {
    if (!listing) return null;
    const publicData = listing?.attributes?.publicData || {};
    const hideOnLandingPage = publicData.hideOnLandingPage;
    if (
      hideOnLandingPage === true ||
      hideOnLandingPage === 'true' ||
      hideOnLandingPage === '1' ||
      hideOnLandingPage === 1
    ) {
      return null;
    }

    const { title: listingTitle, price } = listing.attributes || {};
    const { connectivity, brand, sales, original_price: originalPrice } = publicData;
    const images = listing.images || [];
    const firstImage = images[0] || null;

    const isOnSale = sales === 1 || sales === true || sales === 'true' || sales === '1';
    const priceForDisplay =
      isOnSale || !price
        ? price
        : getListingPriceWithReolinkPromoMaybe(listing, {
            currentUser,
            locationSearch: location.search,
          }) || price;

    let formattedPrice = '';
    try {
      if (priceForDisplay?.amount && priceForDisplay?.currency) {
        formattedPrice = formatMoney(intl, priceForDisplay);
      }
    } catch (_) {
      formattedPrice = priceForDisplay?.amount
        ? `${priceForDisplay.amount / 100} ${priceForDisplay.currency || config?.currency}`
        : '';
    }

    let formattedOriginalPrice = null;
    let formattedMerchantDiscount = null;

    if (isOnSale && originalPrice && price) {
      try {
        const { Money } = sdkTypes;
        const subUnitDivisor = unitDivisor(price.currency);
        const originalInSubunits = convertUnitToSubUnit(originalPrice, subUnitDivisor);
        const originalPriceMoney = new Money(originalInSubunits, price.currency);
        formattedOriginalPrice = formatMoney(intl, originalPriceMoney);
        if (originalInSubunits > price.amount) {
          formattedMerchantDiscount = formatMoney(
            intl,
            new Money(originalInSubunits - price.amount, price.currency)
          );
        }
      } catch (_) {
        // ignore
      }
    }

    const showCampaignPromo =
      !isOnSale &&
      price &&
      priceForDisplay &&
      price.currency === priceForDisplay.currency &&
      price.amount !== priceForDisplay.amount;

    let formattedCampaignListPrice = null;
    let formattedCampaignDiscount = null;
    if (showCampaignPromo) {
      try {
        formattedCampaignListPrice = formatMoney(intl, price);
      } catch (_) {
        formattedCampaignListPrice = null;
      }
      if (price.amount > priceForDisplay.amount) {
        try {
          const { Money } = sdkTypes;
          formattedCampaignDiscount = formatMoney(
            intl,
            new Money(price.amount - priceForDisplay.amount, price.currency)
          );
        } catch (_) {
          formattedCampaignDiscount = null;
        }
      }
    }

    const brandDisplayName = getBrandDisplayName(brand);
    const connectivityDisplay = formatConnectivity(connectivity);
    const listingId = listing.id?.uuid || listing.id;
    const slug = createSlug(listingTitle || 'listing');

    return (
      <NamedLink
        key={listing.id?.uuid || index}
        className={css.carouselProductCard}
        name="ListingPage"
        params={{ id: listingId, slug }}
      >
        {formattedMerchantDiscount ? (
          <span className={css.carouselCardDiscountBadge}>-{formattedMerchantDiscount}</span>
        ) : formattedCampaignDiscount ? (
          <span className={css.carouselCardDiscountBadge}>-{formattedCampaignDiscount}</span>
        ) : null}
        <div className={css.carouselProductImageContainer}>
          {firstImage ? (
            <CarouselProductImage
              src={
                firstImage.attributes?.variants?.['listing-card']?.url ||
                firstImage.attributes?.variants?.['scaled-small']?.url ||
                firstImage.attributes?.variants?.['scaled-medium']?.url ||
                firstImage.attributes?.variants?.default?.url
              }
              alt={listingTitle}
              isEager={index < 4}
            />
          ) : (
            <div className={css.noImagePlaceholder}>Aucune image</div>
          )}
        </div>
        <div className={css.carouselProductInfo}>
          <div className={css.carouselProductPriceAndBrand}>
            {brandDisplayName && <div className={css.carouselBrandInfo}>{brandDisplayName}</div>}
            {formattedPrice && (
              <div className={css.carouselProductPriceCol}>
                {showCampaignPromo && formattedCampaignListPrice && (
                  <div className={css.carouselCampaignStrikeRow}>
                    <span className={css.carouselCampaignPill}>-{formattedCampaignDiscount}</span>
                    <span className={css.carouselOriginalStruck}>{formattedCampaignListPrice}</span>
                  </div>
                )}
                <div className={css.carouselProductPrice}>
                  <span className={css.carouselPriceValue}>{formattedPrice}</span>
                </div>
              </div>
            )}
          </div>
          <div className={css.carouselProductMainInfo}>
            <h3 className={css.carouselProductName}>{listingTitle}</h3>
          </div>
          {connectivityDisplay && (
            <div className={css.carouselProductCategoryConnectivity}>
              <span className={css.carouselConnectivityText}>{connectivityDisplay}</span>
            </div>
          )}
        </div>
      </NamedLink>
    );
  };

  const visibleCards = products.filter(Boolean);

  const renderSkeletonCard = index => (
    <div key={`skeleton-${index}`} className={css.skeletonCard} aria-hidden="true">
      <div className={css.skeletonImage} />
      <div className={css.skeletonInfo}>
        <div className={css.skeletonRow}>
          <div className={classNames(css.skeletonLine, css.skeletonLineBrand)} />
          <div className={classNames(css.skeletonLine, css.skeletonLinePrice)} />
        </div>
        <div className={classNames(css.skeletonLine, css.skeletonLineTitle)} />
        <div className={classNames(css.skeletonLine, css.skeletonLineTitleShort)} />
        <div className={classNames(css.skeletonLine, css.skeletonLineConnectivity)} />
      </div>
    </div>
  );

  return (
    <section className={css.section}>
      <div className={css.container}>
        <div className={css.sectionHeader}>
          <h2 className={css.sectionTitle}>{title}</h2>
          {subtitle && <p className={css.sectionSubtitle}>{subtitle}</p>}
          {viewAllHref && (
            <a href={viewAllHref} className={css.viewAllButton}>
              {viewAllHeaderText}
            </a>
          )}
        </div>

        {inProgress ? (
          <div
            className={css.carouselContainer}
            role="status"
            aria-live="polite"
            aria-busy="true"
            aria-label={`Chargement de ${title.toLowerCase()}`}
          >
            <div className={css.productCarousel}>
              {Array.from({ length: 6 }).map((_, i) => renderSkeletonCard(i))}
            </div>
          </div>
        ) : error ? (
          <div className={css.error}>
            <p>Impossible de charger {title.toLowerCase()}. Veuillez réessayer plus tard.</p>
          </div>
        ) : visibleCards.length > 0 ? (
          <div
            className={classNames(css.carouselContainer, { [css.carouselAtEnd]: !canScrollRight })}
          >
            <button
              className={classNames(css.carouselArrow, css.carouselArrowLeft)}
              onClick={scrollLeft}
              disabled={!canScrollLeft}
              aria-label="Faire défiler à gauche"
            >
              <svg viewBox="0 0 24 24" fill="currentColor">
                <path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z" />
              </svg>
            </button>
            <button
              className={classNames(css.carouselArrow, css.carouselArrowRight)}
              onClick={scrollRight}
              disabled={!canScrollRight}
              aria-label="Faire défiler à droite"
            >
              <svg viewBox="0 0 24 24" fill="currentColor">
                <path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z" />
              </svg>
            </button>
            <div ref={carouselRef} className={css.productCarousel}>
              {visibleCards.map((listing, index) => renderCard(listing, index))}
              {viewAllHref && (
                <a
                  href={viewAllHref}
                  className={classNames(css.carouselProductCard, css.viewMoreCard)}
                >
                  <div className={css.viewMoreGlow} />
                  <div className={css.viewMoreInner}>
                    <div className={css.viewMoreIconCircle}>
                      <svg
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        width="22"
                        height="22"
                      >
                        <path d="M5 12h14M12 5l7 7-7 7" />
                      </svg>
                    </div>
                    <span className={css.viewMoreLabel}>{viewAllCardText}</span>
                    <span className={css.viewMoreSub}>Explorer la sélection</span>
                  </div>
                </a>
              )}
            </div>
          </div>
        ) : (
          <div className={css.noProducts}>
            <p>Aucun produit disponible pour le moment.</p>
          </div>
        )}
      </div>
    </section>
  );
};

ProductCarouselSection.propTypes = {
  products: arrayOf(object),
  inProgress: bool,
  error: object,
  title: string.isRequired,
  subtitle: string,
  categoryLink: string,
  viewAllLabel: string,
  config: object,
  intl: object,
};

export default ProductCarouselSection;
